import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { reportsAPI, exportAPI } from '../lib/api';
import { formatCurrency, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon, FileText, Download, TrendingUp, TrendingDown, PiggyBank, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { es } from 'date-fns/locale';

const Reports = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await reportsAPI.generate({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        report_type: 'monthly'
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Error al generar informe');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await exportAPI.excel({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        report_type: 'monthly'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trebol_finance_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al exportar Excel');
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await exportAPI.pdf({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        report_type: 'monthly'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trebol_finance_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al exportar PDF');
    }
  };

  const setMonthRange = () => {
    setStartDate(startOfMonth(new Date()));
    setEndDate(endOfMonth(new Date()));
  };

  const setYearRange = () => {
    setStartDate(startOfYear(new Date()));
    setEndDate(endOfYear(new Date()));
  };

  const chartData = report?.category_breakdown?.map(cat => ({
    name: cat.category,
    ingresos: cat.income,
    gastos: cat.expense
  })) || [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            {t('reports')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Análisis detallado de tus finanzas
          </p>
        </div>
      </div>

      {/* Date Selection */}
      <Card className="border-slate-200 rounded-sm shadow-none" data-testid="date-selection-card">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-400">
                {t('startDate')}
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-48 justify-start rounded-sm" data-testid="start-date-picker">
                    <CalendarIcon size={16} className="mr-2 text-slate-400" />
                    {format(startDate, 'PPP', { locale: es })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => date && setStartDate(date)}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-400">
                {t('endDate')}
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-48 justify-start rounded-sm" data-testid="end-date-picker">
                    <CalendarIcon size={16} className="mr-2 text-slate-400" />
                    {format(endDate, 'PPP', { locale: es })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => date && setEndDate(date)}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={setMonthRange} className="flex-1 sm:flex-none rounded-sm">
                Este mes
              </Button>
              <Button variant="outline" onClick={setYearRange} className="flex-1 sm:flex-none rounded-sm">
                Este año
              </Button>
            </div>

            <Button
              onClick={generateReport}
              className="w-full sm:w-auto bg-emerald-900 hover:bg-emerald-800 rounded-sm"
              disabled={loading}
              data-testid="generate-report-btn"
            >
              {loading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <FileText size={16} className="mr-2" />}
              {t('generateReport')}
            </Button>

            {report && (
              <>
                <Button
                  variant="outline"
                  onClick={exportToExcel}
                  className="rounded-sm"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet size={16} className="mr-2" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={exportToPDF}
                  className="rounded-sm"
                  data-testid="export-pdf-btn"
                >
                  <Download size={16} className="mr-2" />
                  PDF
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="report-summary">
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                      {t('totalIncome')}
                    </p>
                    <p className="font-mono text-2xl font-medium text-emerald-600">
                      {formatCurrency(report.summary.total_income, user?.currency)}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-emerald-50 rounded-sm flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                      {t('totalExpenses')}
                    </p>
                    <p className="font-mono text-2xl font-medium text-red-500">
                      {formatCurrency(report.summary.total_expenses, user?.currency)}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-red-50 rounded-sm flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                      {t('netSavings')}
                    </p>
                    <p className={cn(
                      "font-mono text-2xl font-medium",
                      report.summary.savings >= 0 ? "text-emerald-600" : "text-red-500"
                    )}>
                      {formatCurrency(report.summary.savings, user?.currency)}
                    </p>
                  </div>
                  <div className={cn(
                    "w-10 h-10 rounded-sm flex items-center justify-center",
                    report.summary.savings >= 0 ? "bg-emerald-50" : "bg-red-50"
                  )}>
                    <PiggyBank className={cn(
                      "w-5 h-5",
                      report.summary.savings >= 0 ? "text-emerald-600" : "text-red-500"
                    )} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                      {t('savingsRate')}
                    </p>
                    <p className={cn(
                      "font-mono text-2xl font-medium",
                      report.summary.savings_rate >= 0 ? "text-emerald-600" : "text-red-500"
                    )}>
                      {report.summary.savings_rate}%
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {report.transaction_count} transacciones
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Category Breakdown Chart */}
          <Card className="border-slate-200 rounded-sm shadow-none" data-testid="category-chart">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-900">
                Desglose por Categoría
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 12, fill: '#64748B' }}
                        tickFormatter={(v) => `${v / 1000}k`}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 12, fill: '#64748B' }}
                        width={100}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value, user?.currency)}
                        contentStyle={{ borderRadius: '2px', border: '1px solid #E2E8F0' }}
                      />
                      <Legend />
                      <Bar dataKey="ingresos" fill="#10B981" name="Ingresos" />
                      <Bar dataKey="gastos" fill="#EF4444" name="Gastos" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">
                  No hay datos para mostrar
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Table */}
          <Card className="border-slate-200 rounded-sm shadow-none" data-testid="category-table">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-900">
                Detalle por Categoría
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left p-4 text-xs uppercase tracking-widest font-semibold text-slate-400">
                        Categoría
                      </th>
                      <th className="text-right p-4 text-xs uppercase tracking-widest font-semibold text-slate-400">
                        Ingresos
                      </th>
                      <th className="text-right p-4 text-xs uppercase tracking-widest font-semibold text-slate-400">
                        Gastos
                      </th>
                      <th className="text-right p-4 text-xs uppercase tracking-widest font-semibold text-slate-400">
                        Neto
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.category_breakdown.map((cat, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="p-4 font-medium text-slate-900">{cat.category}</td>
                        <td className="p-4 text-right font-mono text-emerald-600">
                          {formatCurrency(cat.income, user?.currency)}
                        </td>
                        <td className="p-4 text-right font-mono text-red-500">
                          {formatCurrency(cat.expense, user?.currency)}
                        </td>
                        <td className={cn(
                          "p-4 text-right font-mono font-medium",
                          (cat.income - cat.expense) >= 0 ? "text-emerald-600" : "text-red-500"
                        )}>
                          {formatCurrency(cat.income - cat.expense, user?.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!report && !loading && (
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center h-48 text-slate-400">
            <FileText size={32} className="mb-2" />
            <p>Selecciona un rango de fechas y genera un informe</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
